import { useContext, useEffect, useState } from "react";
import { TopNavigationWrapper } from "./styled";
import { AppContext } from "../../../context";
import {
	Box,
	IconButton,
	InputAdornment,
	SelectChangeEvent,
	Stack,
	Toolbar,
	Typography,
	useMediaQuery,
} from "@mui/material";
import { BaseInput } from "../../../component/form/input/styled";
import { SearchIcon } from "../../../asset";
import MenuIcon from "@mui/icons-material/Menu";
import { SideNavigationPropsType } from "../../../type/container.type";
import { BaseContentModal } from "../../../component/modal/content";
import { useDebounce } from "use-debounce";
import { useNavigate } from "react-router-dom";
import Cookies from "universal-cookie";
import { searchProductService } from "../../../util/product/searchProduct";
import { BaseFieldSet } from "../../../component/form/fieldset/styled";
import { BaseButton } from "../../../component/button/styled";

export const TopNavigation: React.FC<SideNavigationPropsType> = ({
	username,
	avatar,
	role,
}) => {
	const cookies = new Cookies();
	const TOKEN = cookies.getAll().TOKEN;

	const {
		isSideNavigationClosing,
		isMobileSideNavigationOpen,
		setIsMobileSideNavigationOpen,
		isContentModalOpen,
		setIsContentModalOpen,
	} = useContext(AppContext);

	const navigate = useNavigate();
	const matchesTabletAndAbove = useMediaQuery("(min-width:768px)");

	const [searchResult, setSearchResult] = useState<{
		data: Record<string, any>[];
		pagination: Record<string, any>;
	} | null>(null);
	const [formDetails, setFormDetails] = useState({
		query: "",
	});
	const [paginationIndex, setPaginationIndex] = useState({
		page: "1",
		limit: "5",
	});
	const [isSearchResultLoading, setIsSearchResultLoading] = useState(false);

	const [debouncedQueryRef] = useDebounce(formDetails.query, 500);

	useEffect(() => {
		if (!debouncedQueryRef) return setSearchResult(null);
		const controller = new AbortController();
		let isActive = true;
		const searchProduct = async () => {
			try {
				setIsSearchResultLoading(true);
				setSearchResult(null);
				const result = await searchProductService(
					TOKEN,
					{
						q: debouncedQueryRef,
						...paginationIndex,
					},
					controller.signal,
				);
				if (!isActive) return;
				setSearchResult(result);
			} catch (err: any) {
				if (err.name !== "AbortError") {
					console.error("Failed to fetch product:", err);
					setSearchResult(null);
				}
			} finally {
				if (isActive) setIsSearchResultLoading(false);
			}
		};
		searchProduct();
		return () => {
			isActive = false;
			controller.abort();
		};
	}, [TOKEN, debouncedQueryRef, paginationIndex]);

	const handleDrawerToggle = () => {
		if (!isSideNavigationClosing) {
			setIsMobileSideNavigationOpen(!isMobileSideNavigationOpen);
		}
	};

	// const handleOpenNotification = (
	// 	e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
	// ) => {
	// 	e.preventDefault();
	// };

	const handleChange = (
		e:
			| React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
			| SelectChangeEvent<unknown>,
	) => {
		const { name, value } = e.target;
		setFormDetails((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	const handleSearchResultContentModalClose = () => {
		setPaginationIndex((prev) => {
			const newPage = 1;
			return {
				...prev,
				page: newPage.toString(),
			};
		});
		return setIsContentModalOpen(false);
	};

	const handleNext = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
		e.preventDefault();
		setPaginationIndex((prev) => {
			const currentPage = parseInt(prev.page, 10);
			const newPage = Math.min(
				parseInt(searchResult?.pagination?.totalPages, 10),
				currentPage + 1,
			);
			return {
				...prev,
				page: newPage.toString(),
			};
		});
	};

	const handleNavigateToAllProducts = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
	) => {
		setIsContentModalOpen(false);
		return navigate("/inventory");
	};

	const handleNavigateToProductDetail = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		id: string,
	) => {
		setIsContentModalOpen(false);
		return navigate(`/inventory/${id}`);
	};

	const handleSearchResultModalCallToActionClick = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
	) => {
		e.stopPropagation();
		debouncedQueryRef && searchResult && searchResult?.data?.length >= 1
			? handleNext(e)
			: handleNavigateToAllProducts(e);
		return;
	};

	const handleSearchResultContentModalOpen = (
		e:
			| React.FormEvent<HTMLFormElement>
			| React.MouseEvent<HTMLDivElement, MouseEvent>,
	) => {
		e.preventDefault();
		e.stopPropagation();
		return setIsContentModalOpen(true);
	};

	const searchResultBody = (
		<Stack
			gap={"calc(var(--flex-gap)/2)"}
			padding={"0 calc(var(--basic-padding))"}
		>
			{debouncedQueryRef && searchResult && searchResult?.data?.length >= 1 ? (
				searchResult?.data?.map((product, index) => (
					<Stack
						key={index}
						sx={{
							borderRadius: "12px",
							gap: "calc(var(--flex-gap)/3)",
							padding: "calc(var(--basic-padding)/4)",
							border: "1px solid var(--border-faint-primary-color)",
						}}
					>
						<Stack
							sx={{
								overflow: "hidden",
								flexDirection: "row",
								gap: "calc(var(--flex-gap)/3)",
								"& .product-thumbnail-box": {
									height: "11.25rem",
									display: "flex",
									overflow: "hidden",
									borderRadius: "6px",
									flex: 1,
									"& img": {
										width: "100%",
										height: "100%",
										borderRadius: "inherit",
									},
								},
							}}
						>
							<Box component={"div"} className="product-thumbnail-box">
								<img src={product?.images?.[0]} alt={product?.name} />
							</Box>
							<Stack
								flex={1}
								overflow={"hidden"}
								gap={"calc(var(--flex-gap)/8)"}
							>
								<Box>
									<Typography
										variant="caption"
										fontFamily={"Inter"}
										fontWeight={600}
										fontSize={12}
										lineHeight={"normal"}
										whiteSpace={"normal"}
										color={"var(--subtitle-gray-color)"}
										display={"inline-block"}
										width={"100%"}
									>
										{product?.category}
									</Typography>
									<Typography
										variant="h3"
										component={"h3"}
										fontFamily={"Inter"}
										fontWeight={600}
										fontSize={16}
										lineHeight={"normal"}
										whiteSpace={"normal"}
										sx={{ cursor: "pointer" }}
										color={"var(--off-primary-color)"}
									>
										{product?.name}
									</Typography>
								</Box>
								<Box>
									<Typography
										variant="caption"
										fontFamily={"Inter"}
										fontWeight={600}
										fontSize={16}
										lineHeight={"normal"}
										whiteSpace={"normal"}
										color={"var(--off-primary-color)"}
										display={"inline-block"}
										width={"100%"}
									>
										{`₦${product?.price?.toLocaleString()}`}
									</Typography>
								</Box>
							</Stack>
						</Stack>
						<Box sx={{ display: "flex", overflow: "hidden" }}>
							<BaseButton
								disableElevation
								variant="contained"
								sx={{
									width: "100%",
									cursor: "pointer",
									borderRadius: "6px",
									padding: "calc(var(--basic-padding)/3)",
								}}
								onClick={(e) => handleNavigateToProductDetail(e, product?.id)}
							>
								<Typography
									variant={"button"}
									fontFamily={"inherit"}
									fontWeight={"inherit"}
									fontSize={"inherit"}
									lineHeight={"inherit"}
									color={"inherit"}
									textTransform={"inherit"}
								>
									See Details
								</Typography>
							</BaseButton>
						</Box>
					</Stack>
				))
			) : (
				<Stack overflow={"hidden"} gap={"calc(var(--flex-gap)/8)"}>
					<Box>
						<Typography
							variant="body1"
							fontFamily={"Inter"}
							fontWeight={500}
							fontSize={14}
							lineHeight={"normal"}
							whiteSpace={"normal"}
							color={"var(--text-gray-color)"}
							textAlign={"center"}
						>
							No Results, try again
						</Typography>
					</Box>
				</Stack>
			)}
		</Stack>
	);

	return (
		<TopNavigationWrapper>
			<BaseContentModal
				body={searchResultBody}
				title={"Search Results"}
				open={isContentModalOpen}
				subtitle={debouncedQueryRef}
				isLoading={isSearchResultLoading}
				className="navigation search-result-content-modal"
				handleClose={handleSearchResultContentModalClose}
				handleCallToAction={handleSearchResultModalCallToActionClick}
				callToAction={
					debouncedQueryRef && searchResult && searchResult?.data?.length >= 1
						? `View More Results (${searchResult?.pagination?.page ?? 0}/${searchResult?.pagination?.totalPages ?? 0})`
						: "View All Products"
				}
			/>
			<Toolbar>
				<Box
					component={"form"}
					className="top-navigation-LHS"
					onSubmit={handleSearchResultContentModalOpen}
				>
					<BaseFieldSet>
						<BaseInput
							name="query"
							value={formDetails.query}
							endAdornment={
								<InputAdornment
									position="end"
									sx={{ cursor: "pointer" }}
									onClick={(e) => handleSearchResultContentModalOpen(e)}
								>
									<SearchIcon />
								</InputAdornment>
							}
							placeholder="Search Products"
							onChange={(e) => handleChange(e)}
							bgcolor="var(--input-field-bg-color)"
							borderradius={matchesTabletAndAbove ? "19px" : "6px"}
							padding="calc(var(--basic-padding)/8) calc(var(--basic-padding)/2)"
						/>
					</BaseFieldSet>
				</Box>
				<Stack className="top-navigation-MS">
					{/* <Box component={"div"} className="notification-button-box">
						<IconButton
							sx={{
								color: "#FAF6F6",
								border: "1px solid #FAF6F6",
								borderRadius: "50%",
							}}
							onClick={handleOpenNotification}
						>
							<NotificationIcon />
						</IconButton>
					</Box> */}
					<Stack className="logged-in-user-information-stack">
						<Box>
							<img src={avatar} alt="user-avatar" className="user-avatar" />
						</Box>
						<Stack>
							<Box>
								<Typography
									variant="subtitle1"
									fontFamily={"Gilroy"}
									fontWeight={600}
									fontSize={14}
									lineHeight={"normal"}
									color={"var(--input-field-text-color)"}
									maxWidth={"64px"}
									marginBlockEnd={"calc(var(--basic-margin)/4)"}
								>
									{username}
								</Typography>
							</Box>
							<Box>
								<Typography
									variant="subtitle2"
									fontFamily={"Gilroy"}
									fontWeight={400}
									fontSize={12}
									lineHeight={"normal"}
									maxWidth={"64px"}
									color={"var(--input-field-text-color)"}
								>
									{role}
								</Typography>
							</Box>
						</Stack>
					</Stack>
				</Stack>
				<Box component={"div"} className="top-navigation-RHS">
					<IconButton
						size="large"
						color="inherit"
						aria-label="menu"
						sx={{
							borderRadius: "6px",
							color: "var(--light-color)",
							padding: "calc(var(--basic-padding)/8)",
							backgroundColor: "var(--primary-color)",
						}}
						onClick={handleDrawerToggle}
					>
						<MenuIcon />
					</IconButton>
				</Box>
			</Toolbar>
		</TopNavigationWrapper>
	);
};
